import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { createGroupWidget } from 'actions/group';
import { isWidgetLocked, useSelectedWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { EditorContext } from 'components/Editor';
import { Property } from '../../components/properties/';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import { bulkUpdateWidgetLayouts, toggleEditMode, updateBulkWidgetProperty, splitPageBySelection } from 'actions/editor';

import { WidgetRegistry } from 'components/Editor/widgets/registry';

import { BUTTON, FORM, HTML, IFRAME, IMAGE, LINE, SHAPE, TEXT, VIDEO, GROUP, RECTANGLE, MENU_CONTAINER, HOTSPOT, CONTENT_CARD, SEEK, EXTERNAL_FORM, SECTION, MENU, CAROUSEL, LIVE_ELEMENT_CONTAINER, LIVE_ELEMENT, PAGE, ANCHOR } from 'constants/widgetTypes';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';

import userRoleService from 'services/userRoles.service';
import { actionsList, GroupPropertyPanel, GroupWidgetActions } from 'components/Editor/helper/group';
import { getBoundingRectFromLayout, isSelectionIndependentHorizontally, hasContentAboveOrBelowSelection } from 'components/Editor/helper/layoutManager';
import StoryboardContext from 'components/Storyboard/context';

import styles from './toolbar.module.scss';
import { SEGMENT_STORYBOARDS } from 'constants/storyboardType';

const ALLOWED_LIVE_ELEMENTS = [
  TEXT, BUTTON, SHAPE, LINE, FORM, IMAGE, VIDEO, HTML, IFRAME, RECTANGLE
];

const ALLOWED_GROUP = [
  TEXT, BUTTON, SHAPE, LINE, IMAGE, VIDEO, HTML, IFRAME, GROUP, RECTANGLE
]

const ALLOWED_BRANDKIT = [
  TEXT, BUTTON, SHAPE, LINE, IMAGE, VIDEO, HTML, IFRAME, GROUP, RECTANGLE, FORM, EXTERNAL_FORM, RECTANGLE, SECTION, MENU, CAROUSEL, SEEK, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER
];

function getPageAncestor(widgets, widget) {
  let w = widget;
  while (w && w.parent) {
    const parent = widgets.find(item => item.uid === w.parent);
    if (!parent) return null;
    if (parent.type === PAGE) return parent.uid;
    w = parent;
  }
  return null;
}

function WidgetToolBar({ rect, showActions }) {
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const [dragStyles, setDragStyles] = useState({
    left: -8,
    bottom: 0
  });

  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);

  const widget = useSelector(state => {
    const selected = _.get(state, `${editorSession}.present.selected`);
    if (!selected || selected.length > 1) {
      return null;
    }

    const id = selected[0];
    return widgets.find(item => item.uid === id);
  });

  const isWidgetLocked = useSelector(state => {
    if (!widget) return false;

    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    // recursive function
    const checkLocked = (w) => {
      if (!w) return false;

      const locked = _.get(w, `properties.${breakpoint}.locked`, false);
      if (locked) return true;

      const parent = widgets.find(i => i.uid === w.parent);
      return checkLocked(parent);
    };

    return checkLocked(widget);
  });

  useEffect(() => {
    var left = -8;
    var bottom = 0;

    if (rect.left < 30) {
      left = -rect.left + 30;
      bottom = -25;
    }

    if (rect.top < 0) {
      bottom = rect.top - 20;
    }

    setDragStyles({
      left: left,
      bottom: bottom
    });
  }, [rect]);

  if (!widget || isWidgetLocked) {
    return false;
  }

  return (
    <div className={cx('moveable-widget-toolbar', styles.toolbar)}>
      {showActions && 
        <div className={cx('moveable-widget-toolbar', styles.actions)}>
          {WidgetRegistry.actions(editorSession, widget.type, breakpoint, widget)}
        </div>
      }
      { (widget.type !== MENU_CONTAINER) && (widget.type === SECTION) &&
        <div className={cx('moveable-drag-target', styles.dragTarget)} style={dragStyles} >
          <FontAwesomeIcon icon={faExpandArrowsAlt} />
        </div>
      }
      <TruncatedTextWithTooltip className={cx(styles.title)} content={widget.name} maxWidth={150} placement='top'>
        {widget.name}
      </TruncatedTextWithTooltip>
    </div>
  );
}

const GroupToolBar = ({ elements }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);
  const editorContext = React.useContext(EditorContext);
  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');
  const editorSession = editorContext.editorSession.id;
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const selectedWidgets = useSelectedWidgetSelector(editorSession);
  var widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const batchEdit = true;

  const crop = _.get(selectedWidgets?.[0], `properties.${breakpoint}.crop`, {}); 

  // handle edit mode
  const handleEditMode = (view) => {
    if (actionsList.includes(view)) {
      setView(view);
      return;
    }

    if (view === "makeFrame") {
      dispatch(splitPageBySelection(editorSession, makePageInfo.pageId, selectedWidgets.map(w => w.uid)))
      return;
    }

    if (view === "group") {
      dispatch(createGroupWidget(editorSession, selectedWidgets));
      return;
    }

    dispatch(
      toggleEditMode(
        editorSession,
        selectedWidgets[0]?.uid,
        true,
        view,
        batchEdit
      )
    );
  };

  const onCrop = (crop, layoutValue) => {
    const widgetIds = selectedWidgets.map((w) => w.uid);
    dispatch(updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, crop));
    
    if (breakpoint !== 'mobile') {
      dispatch(updateBulkWidgetProperty(editorSession, widgetIds, 'mobile', crop));
    }

    const updatedLayouts = selectedWidgets.map(widget => {
      const uid = _.get(widget, 'uid');
      const layout = _.get(widget, `properties.${breakpoint}.layout`);

      const frame = new Frame(layout);
      const parsedRect = getBoundingRectFromLayout(layout);

      const {
        deltaX,
        deltaY,
        width,
        height
      } = layoutValue[uid].layout || {};
  
      frame.set('transform', 'translate', `${parsedRect.x + deltaX}px, ${parsedRect.y + deltaY}px`);
      frame.set('width', `${width}px`);
      frame.set('height', `${height}px`);
      return {
        id: widget.uid,
        layout: frame.get()
      }
    });

    dispatch(bulkUpdateWidgetLayouts(editorSession, breakpoint, updatedLayouts));
    setView(null);
  }

  const canCreateLiveElement = React.useMemo(() => {
    var allowed = entityType !== 'LIVE_ELEMENT';

    selectedWidgets.forEach(widget => {
      allowed = allowed && _.includes(ALLOWED_LIVE_ELEMENTS, widget.type) 
        && !(widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT))
        && !(widgets.some(w => w.uid === widget.parent && w.type === HOTSPOT));
    });

    var parents = selectedWidgets.map(el => el.parent);
    parents = _.uniq(parents);

    allowed = allowed && parents.some(parent => {
      var widget = widgets.find(w => w.uid === parent);
      return ![GROUP, CONTENT_CARD].includes(widget.type);;
    });

    return allowed && _.size(parents) == 1;
    
  }, [selectedWidgets, widgets]);

  const canGroupWidgets = React.useMemo(() => {
    var allowed = true;

    var parents = selectedWidgets.map(el => el.parent).filter(el => el !== null);
    parents = _.uniq(parents);

    if (_.size(parents) === 1) {
      var parent = widgets.find(w => w.uid === parents[0]);
      allowed = ![GROUP, CONTENT_CARD].includes(parent.type);
    }

    selectedWidgets.forEach(widget => {
      allowed = allowed && _.includes(ALLOWED_GROUP, widget.type) 
        && !(widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT));
    });

    return allowed && _.size(parents) === 1;
  }, [selectedWidgets, widgets]);

  const canBrandKitWidgets = React.useMemo(() => {
    var allowed = entityType !== 'PALETTE_COMPONENT';

    if(!allowed) return false;
    
    var parents = selectedWidgets.flatMap(el => el.parent).filter(el => el !== null);
    parents = _.uniq(parents);

    if (_.size(parents) === 1) {
      var parent = widgets.find(w => w.uid === parents[0]);
      allowed = ![CONTENT_CARD].includes(parent.type);
    }

    selectedWidgets.forEach(widget => {
      allowed = allowed && _.includes(ALLOWED_BRANDKIT, widget.type);
    });

    return allowed && brandkitEnabled && _.size(parents) === 1;
  }, [selectedWidgets, brandkitEnabled, widgets]);


  const isWidgetLocked = (widgets, widget, breakpoint) => {
    if (!widget) return false;

    // check current widget
    const locked = _.get(widget, `properties.${breakpoint}.locked`, false);
    if (locked) return true;

    // walk up to parent
    const parent = widgets.find(w => w.uid === widget.parent);

    if (!parent) return false; // reached top

    return isWidgetLocked(widgets, parent, breakpoint);
  }

  const isParentLocked = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    // If ANY selected widget has a locked ancestor → return true
    return selectedWidgets.some(widget =>
      isWidgetLocked(widgets, widget, breakpoint)
    );
  });

  const makePageInfo = React.useMemo(() => {
    if (
      !SEGMENT_STORYBOARDS.includes(storyboardType) ||
      entityType === 'GATING' ||
      selectedWidgets.length === 0
    ) {
      return { show: false, pageId: null };
    }

    /** SAME PAGE/FRAME CHECK */
    const pageIds = selectedWidgets.map(w => getPageAncestor(widgets, w));
    const allSamePage = pageIds.every(id => id === pageIds[0]) && pageIds[0] != null;
    if (!allSamePage) return { show: false, pageId: null };

    /** HORIZONTAL POSITION CHECK */
    const pageId = pageIds[0];
    const selectedIds = selectedWidgets.map(w => w.uid);

    const hasAnchorWidget = selectedWidgets.filter((w) => w.type === ANCHOR);

    if(hasAnchorWidget?.length) {
      return { show: false, pageId: null };
    }

    if (!isSelectionIndependentHorizontally(editorSession, pageId, selectedIds, widgets)) {
      return { show: false, pageId: null };
    }
    if (!hasContentAboveOrBelowSelection(editorSession, pageId, selectedIds, widgets)) {
      return { show: false, pageId: null };
    }
    return { show: true, pageId };
  }, [storyboardType, entityType, selectedWidgets, widgets, editorSession]);

  const visibleActions = React.useMemo(() => {
    if(selectedWidgets.length < 2 ) return [];

    const actionsConfig = GroupWidgetActions({ breakpoint, styles }); // call the function with styles

    const actions = Object.values(actionsConfig)
      .filter((config) =>
        config.show({
          widgets: selectedWidgets,
          canBrandKitWidgets,
          canCreateLiveElement,
          canGroupWidgets,
          role: userRoleService.canManageUsers(),
          makeFrame: makePageInfo?.show
        })
      )
      .flatMap((config) =>
        config.actions.map((action) => ({
          label: action.label,
          tooltip: action.tooltip,
          className: action.className,
          onClick: action.onClick(handleEditMode),
        }))
      );

    return actions;
  }, [selectedWidgets, handleEditMode, styles, canBrandKitWidgets, canCreateLiveElement, canGroupWidgets, makePageInfo, dispatch, editorSession, breakpoint]);

  if (isParentLocked) {
    return false;
  }

  return (
    <div className={cx('moveable-widget-toolbar', styles.toolbar)}>
      <div className={styles.actions}>
        {visibleActions.map((action, idx) => (
          <Property.Action
            key={idx}
            tooltip={action.tooltip || ""}
            className={action.className || ""}
            onClick={action.onClick}
          >
            {action.label}
          </Property.Action>
        ))}

        <Property.Divider />
        {canCreateLiveElement && canGroupWidgets && 
          <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
            <i className={'cleverstory-help'} />
          </Property.Action>
        }
      </div>

       <GroupPropertyPanel
          view={view}
          selectedWidgets={selectedWidgets}
          editorSession={editorSession}
          editorContext={editorContext}
          elements={elements}
          setView={setView}
          onCrop={onCrop}
          crop={crop}
        />
    </div>
  )
}

export const ToolBarViewable = {
  name: "toolBarViewable",
  props: {},
  events: {},
  render(moveable, renderer) {
    const elements = moveable.props.targets;
    const isDragging = moveable.props.isDragging;
    const rect = moveable.getRect();

    if (_.size(elements) > 1) {
      return false;
    }

    return (
      <WidgetToolBar key={"widget-toolbar"} rect={rect} showActions={!isDragging} />
    );
  }
}

export const GroupToolBarViewable = {
  name: "groupToolBarViewable",
  props: {},
  events: {},
  render(moveable, renderer) {
    const elements = moveable.props.targets

    if (_.size(elements) == 1) {
      return false;
    }

    return (
      <GroupToolBar key={"group-widget-toolbar"} elements={elements} />
    );
  }
}
